import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

export function BottomSheet({ open, onClose, title, children }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  // Focus management: capture trigger on open, move focus into sheet, restore on close
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      const closeBtn = sheetRef.current?.querySelector<HTMLElement>('.sheet__close');
      if (closeBtn) closeBtn.focus();
      else sheetRef.current?.focus();
    } else {
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
        triggerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  // Trap focus within the sheet
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !sheetRef.current) return;
    const focusable = sheetRef.current.querySelectorAll<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="sheet__backdrop sheet__backdrop--open"
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        className="sheet__panel sheet__panel--open"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className="sheet__handle" />
        {title && (
          <div className="sheet__header">
            <h2 className="sheet__title">{title}</h2>
            <button className="sheet__close" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
        )}
        <div className="sheet__body">
          {children}
        </div>
      </div>
    </>,
    document.body
  );
}
